<template>
  <div ref="table" class="graphql-table-wrapper" :class="{ isFullscreen }">
    <GraphqlDataTableFilters
      v-model:filters="activeFilterValues"
      v-model:show="showFilters"
      :query="stringifiedQuery"
      :filter-black-list="filterBlackList"
    />
    <BaseDataTable
      :entries="entries"
      :columns
      :loading="loading"
      :paginator-info="paginatorInfo"
      :default-page-length="paginationPerPage"
      :prevent-row-selection-on-last-td="!!slots.action"
      data-test="graphql-data-table"
      multi-select-target-key="id"
      :max-height="
        isFullscreen
          ? 'calc(100vh - 76px)'
          : `calc(100vh - 46px - 24px - 56px - 20px - 60px ${maxHeightAdjust})`
      "
      @search="handleTableSearch"
    >
      <template v-if="error && !loading && !loadDataRegardlessOfError" #table-content>
        <WdsStack direction-row justify-center>
          <GraphqlDataTableErrorNotice
            :has-data="entries?.length > 0"
            @load-data="loadDataRegardlessOfError = true"
          />
        </WdsStack>
      </template>
      <template #no-results="{ loading: l }">
        <WdsStack direction-row justify-center>
          <slot v-if="l" name="loading-notice">
            <BaseLoadingNotice class="tw-z-10" data-test="tc-loading">
              <template #main>
                {{ $t('Finding matching data') }}
              </template>
            </BaseLoadingNotice>
          </slot>
          <template v-else-if="!l && !error">
            <slot name="no-results">
              {{ $t('No matching records found') }}
            </slot>
          </template>
        </WdsStack>
      </template>
      <template #control-bar>
        <GraphqlDataTableControlBar
          v-model:search="search"
          v-model:show-filters="showFilters"
          v-model:order-by="orderBy"
          :full-screen-mode="isFullscreen"
          :number-of-items="totalNumberOfItems"
          :num-active-filters="activeFilterValueCounts"
          :query="query"
          @download="showDownloadModal = true"
          @full-screen="toggleFullscreen"
        >
          <template v-if="$slots.filters || quickFilters?.length > 0" #filters>
            <slot name="filters">
              <WdsStack v-if="quickFilters?.length > 0" direction-row justify-center gap-sm>
                <GraphqlDataTableQuickFilter
                  v-if="quickFilters?.[0]"
                  v-model="activeFilterValues[quickFilters[0]]"
                  :query="stringifiedQuery"
                  :argument-string="quickFilters?.[0]"
                />
                <GraphqlDataTableQuickFilter
                  v-if="quickFilters?.[1]"
                  v-model="activeFilterValues[quickFilters[1]]"
                  :query="stringifiedQuery"
                  :argument-string="quickFilters?.[1]"
                />
              </WdsStack>
            </slot>
          </template>
        </GraphqlDataTableControlBar>
      </template>
      <template #action="{ row }">
        <slot name="action" :row="row"></slot>
      </template>
    </BaseDataTable>
    <div class="graphql-table-globals-wrapper">
      <GraphqlDownloadTableModal
        v-model:show="showDownloadModal"
        :query="query"
        :variables="queryVariables"
        :data-description="tableDescription"
      />
      <WdsDrawer
        v-model:show="workerDetailsSidebarVisible"
        position="right"
        dismissible
        width="420px"
        padding="0"
      >
        <WorkerDetailPanel
          :viewer-account-id="workerDetailsProps.viewerAccountId"
          :trusted-contact-id="workerDetailsProps.trustedContactId"
          :worker-id="workerDetailsProps.workerId"
          @close="closeDetails"
          @updated="refetch"
          @deleted="refetch"
        />
      </WdsDrawer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WdsDrawer, WdsStack } from '@worksome/wds-components'
import {
  ExecutableDefinitionNode,
  Kind,
  print,
  SelectionNode,
  TypedQueryDocumentNode,
  TypeKind,
} from 'graphql'
import {
  computed,
  ComputedRef,
  markRaw,
  provide,
  reactive,
  ref,
  useSlots,
  useTemplateRef,
  watch,
} from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { useFullscreen } from '@vueuse/core'
import GraphqlDataTableFilters from '@/components/GraphqlDataTable/GraphqlDataTableFilters.vue'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { convertToPrimitive, dotAccess, findProperty } from '@/utils/tools'
import ColumnMap from '@/components/GraphqlDataTable/columns'
import { GraphqlColumnMap } from '@/components/GraphqlDataTable/columns/types'
import WorkerDetailPanel from '@/components/WorkerDetailPanel/WorkerDetailPanel.vue'
import {
  addOpenWorkerDetailPanelListener,
  OpenWorkerDetailPanelInput,
} from '@/components/GraphqlDataTable/listners'
import { useIntrospectionTypes, useQueryType } from '@/components/GraphqlDataTable/composables'
import { fieldToReadAble, getFieldTypeLeaf } from '@/components/GraphqlDataTable/tools'
import { FieldTypeFragment, FullTypeFragment, SortOrder } from '@/gql/graphql'
import { TdDate, TdText } from '@/components/BaseDataTable/Cells'
import { $t } from '@/composables/translation'
import BaseLoadingNotice from '@/components/BaseLoadingNotice.vue'
import GraphqlDataTableControlBar from '@/components/GraphqlDataTable/GraphqlDataTableControlBar.vue'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import useQueryEncoder from '@/composables/useQueryEncoder'
import GraphqlDownloadTableModal from '@/components/GraphqlDataTable/GraphqlDownloadTableModal.vue'
import { DataDescriber, SheetDescriber, TableDescriber } from '@/utils/apiDataExports'
import GraphqlDataTableErrorNotice from '@/components/GraphqlDataTable/GraphqlDataTableErrorNotice.vue'
import { FieldTypeFragmentDef, TypeRefFragmentDef } from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'
import GraphqlDataTableQuickFilter from '@/components/GraphqlDataTable/filters/GraphqlDataTableQuickFilter.vue'
import { countActiveFilters } from '@/components/GraphqlDataTable/filters/filter-tools'
import { TableFullscreen } from '@/components/GraphqlDataTable/injection'

const slots = useSlots()

const emit = defineEmits<{
  fullScreen: [mode: boolean]
}>()

const table = useTemplateRef<HTMLElement>('table')

const { isFullscreen, enter, exit } = useFullscreen(table)
const toggleFullscreen = (state: boolean) => {
  if (!table.value) return
  emit('fullScreen', state)
  if (state) {
    enter()
  } else {
    exit()
  }
}

provide(TableFullscreen, isFullscreen)

interface DataTableProps {
  query: TypedQueryDocumentNode
  clientId?: string
  filterBlackList?: string[]
  quickFilters?: readonly [string?, string?] // A maximum of 2 quick filters
  maxHeightAdjustment?: number | string
}

const props = withDefaults(defineProps<DataTableProps>(), {
  clientId: 'publicApi',
  maxHeightAdjustment: '',
  filterBlackList: undefined,
  quickFilters: undefined,
})

const { clientId } = props

const stringifiedQuery = computed(() => print(props.query))

const { args: queryArgs } = useQueryType(stringifiedQuery)

const maxHeightAdjust = computed(() => {
  if (!props.maxHeightAdjustment) return ''
  if (typeof props.maxHeightAdjustment === 'number') return `- ${props.maxHeightAdjustment}px`
  return `- ${props.maxHeightAdjustment}`
})

const searchArg = computed(() =>
  queryArgs.value.inputtedArgs.filter((arg) => !!arg).find(({ name }) => name === 'search'),
)
const paginationPage = useQueryEncoder('page', 1, (raw) =>
  raw === '' ? 1 : convertToPrimitive(raw, Number as any),
)
const paginationPerPage = useQueryEncoder('per-page', 50, (raw) =>
  raw === '' ? 50 : convertToPrimitive(raw, Number as any),
)

const search = useQueryEncoder('search', '')

const ob = defineModel<{ field: string; order: SortOrder | null } | undefined>('orderBy')
const orderBy = useQueryEncoder(
  'orderBy',
  ob,
  (raw) => {
    if (raw === '') return ob.value
    return JSON.parse(atob(raw))
  },
  (data) => btoa(JSON.stringify(data)),
)

const defaultBlacklist = ['search', 'orderBy']

const filterBlackList = computed(() => [
  ...new Set([...(props.filterBlackList || []), ...defaultBlacklist]),
])

const activeFilterValues = reactive({})

watch([search, activeFilterValues], () => {
  paginationPage.value = 1 // Any time search changes we reset the page to the first
})

const queryVariables = computed(() => {
  const filters: Record<string, any> = Object.keys(activeFilterValues)
    .map((key) => [
      key,
      activeFilterValues[key]?.map?.(({ value }) => value) ?? activeFilterValues[key],
    ])
    .filter(
      // Filter out empty arrays and falsy values
      ([_, value]) =>
        (Array.isArray(value) && value.length > 0) ||
        (!Array.isArray(value) && !!value) ||
        value === false,
      // For boolean values we might need  to take into consideration if the values are required
      // || value === false, // We are okay with the pure false value.
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  if (searchArg.value) {
    filters.search = search.value
  }

  return {
    // Default to always have the current account but can be overwritten by filters
    accounts: [currentAccount.globalId],
    ...filters,
    page: paginationPage.value,
    first: paginationPerPage.value,
    orderBy: orderBy.value ? [orderBy.value] : [],
  }
})

const activeFilterValueCounts = computed(() =>
  countActiveFilters(Object.values(activeFilterValues)),
)

const loadDataRegardlessOfError = ref(false)

watch(queryVariables, () => {
  loadDataRegardlessOfError.value = false
})

const { result, error, loading, refetch } = useQuery(props.query, () => queryVariables.value, {
  clientId,
  errorPolicy: 'all',
})

const paginatorInfo = computed(() => findProperty('paginatorInfo', result.value))
const totalNumberOfItems = computed(() => paginatorInfo.value?.total ?? 0)

const showFilters = ref(false)

const OperationDefinition = 'OperationDefinition'
const BlackListFragments = ['PaginatorInfoFragment']

const { typeLookup } = useIntrospectionTypes()

const findDataField = (field: SelectionNode | ExecutableDefinitionNode) => {
  if (field.kind === Kind.INLINE_FRAGMENT) return null
  if (field.name?.value === 'data') return field
  if (field.kind === Kind.FRAGMENT_SPREAD || !field.selectionSet) return null
  for (const fieldElement of field.selectionSet.selections) {
    const res = findDataField(fieldElement)
    if (res !== null) return res
  }
  return null
}

const { type: queryType } = useQueryType(stringifiedQuery)

const findRootType = (): null | FullTypeFragment => {
  if (!queryType.value) return null
  const type = useFragment(TypeRefFragmentDef, queryType.value.type)
  const queryTypeLeaf = getFieldTypeLeaf(type)
  if (!queryTypeLeaf || !(queryTypeLeaf.name !== null && queryTypeLeaf.name !== undefined))
    return null
  const queryName = queryTypeLeaf.name
  const queryField = typeLookup.value[queryName]
  if (!queryField || !queryField.fields) return null
  const dataField = queryField.fields.find(
    (field) => useFragment(FieldTypeFragmentDef, field).name === 'data',
  )
  if (!dataField) return null
  // Return the name of the innermost type of the 'data' field
  const dataName = getFieldTypeLeaf(
    useFragment(TypeRefFragmentDef, useFragment(FieldTypeFragmentDef, dataField).type),
  ).name
  if (!dataName) return null
  return typeLookup.value[dataName] ?? null
}

const fieldToColumn = (field: FieldTypeFragment | undefined): GraphqlColumnMap | null => {
  if (!field) return null
  const type = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type))

  const key = `${field.name}:${type.name}:${type.kind}`
  if (ColumnMap[key]) {
    return ColumnMap[key]
  }

  if ((type.kind as unknown as TypeKind) !== TypeKind.SCALAR) {
    return null
  }

  switch (type.name) {
    case 'Date':
      return {
        component: markRaw(TdDate),
        columnDef: {
          title: fieldToReadAble(field.name),
          key: field.name,
          renderUsing: {
            component: markRaw(TdDate),
            componentProps: (row: any) => ({
              date: row[field.name],
              tooltip: $t(field.description),
            }),
          },
        },
        dataDescriber: [
          {
            title: fieldToReadAble(field.name),
            extraction: (data) => data[field.name],
          },
        ],
      }
    default:
      return {
        component: markRaw(TdText),
        columnDef: {
          title: fieldToReadAble(field.name),
          key: field.name,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row: any) => ({
              text: row[field.name],
            }),
          },
        },
        dataDescriber: [
          {
            title: fieldToReadAble(field.name),
            extraction: (data) => data[field.name],
          },
        ],
      }
  }
}

const columnDefinitions = computed(() => {
  const dataType = findRootType()
  // Get the operation definition
  const definition = props.query.definitions.find(({ kind }) => kind === OperationDefinition)
  if (!definition || !dataType) return [] // Perhaps we should throw an error here or graciously handle the error
  const dataSelections = findDataField(definition)?.selectionSet?.selections ?? []

  return dataSelections.map((field: SelectionNode) => {
    switch (field.kind) {
      case Kind.FRAGMENT_SPREAD:
        return { name: field.name.value, columnDescriber: ColumnMap[field.name.value] }
      case Kind.FIELD:
        // eslint-disable-next-line no-case-declarations
        const fieldType: FieldTypeFragment | undefined = dataType.fields
          ?.map((f) => useFragment(FieldTypeFragmentDef, f))
          .find((ft) => ft.name === field.name.value)
        return { name: field.name.value, columnDescriber: fieldToColumn(fieldType) }
    }
    // Inline fragment case, not implemented as of now
    return {
      name: field.kind,
      columnDescriber: {},
    }
  })
})

const columns = computed(() => {
  const dataColumns = columnDefinitions.value
    .filter(
      ({ name, columnDescriber }) =>
        !BlackListFragments.includes(name) && !!columnDescriber?.columnDef,
    )
    .map(({ columnDescriber }) => {
      if (columnDescriber.sort?.length > 0 && columnDescriber.columnDef.orderAble) {
        const sortingMatch = columnDescriber.sort.find(
          ({ orderByColumn, sortOrder }) =>
            orderByColumn === orderBy.value?.field && sortOrder === orderBy.value?.order,
        )

        if (sortingMatch) {
          // might wanna fix this bit
          // eslint-disable-next-line no-param-reassign
          columnDescriber.columnDef.order = sortingMatch.sortOrder.toLowerCase()
        }
      }

      return columnDescriber.columnDef
    })
  // Adding action column if it exists
  if (slots.action) {
    dataColumns.push({
      title: '',
      key: 'action-column',
      slot: 'action',
      sticky: true,
      ignoreClickForRowSelection: false, // We assume action menus have a click handler of some sort
    })
  }
  return dataColumns
})

const handleTableSearch = ({ page, perPage, order } = { page: 1, perPage: 50, order: {} }) => {
  paginationPage.value = page + 1 // BaseDataTable page numbers are 0 indexed but Gql are 1 indexed
  paginationPerPage.value = perPage

  const cols = columnDefinitions.value
    .map(({ columnDescriber }) => columnDescriber)
    .filter((column) => !!order[column?.columnDef?.key])

  for (const col of cols) {
    // So if we have a custom sort function, we will use that to update our ordering
    if (col.sort?.length > 0) {
      const sortOption: SortOption = col.sort[0]
      orderBy.value = {
        field: sortOption.orderByColumn,
        order: order[col.columnDef.key].toUpperCase(),
      }
    }
  }
}

const entries = computed(() => {
  // Get the operation definition
  const def = props.query.definitions.find(({ kind }) => kind === OperationDefinition)
  const selectionPath = def?.selectionSet.selections?.[0] // We assume only one query path has been made
  if (selectionPath?.kind !== Kind.INLINE_FRAGMENT) {
    const path = `${selectionPath?.name?.value}.data`
    return dotAccess(result.value, path)
  }
  return result.value
})

const showDownloadModal = ref(false)

// Used for data exports
const tableDescription: ComputedRef<TableDescriber<any>> = computed(() => {
  const dataDescriptions: DataDescriber<any>[] = columnDefinitions.value
    .map(({ columnDescriber }) => columnDescriber)
    .flatMap((columDescription: GraphqlColumnMap) => columDescription?.dataDescriber)
    .filter((des) => des)

  const sheetDescription: SheetDescriber<any> = {
    columns: dataDescriptions,
    title: 'Sheet',
  }

  return {
    title: '',
    sheets: [sheetDescription],
  }
})

const workerDetailsSidebarVisible = ref(false)
const workerDetailsProps = reactive({
  workerId: '',
  trustedContactId: '',
  viewerAccountId: currentAccount.globalId,
})

const closeDetails = () => {
  workerDetailsProps.workerId = ''
  workerDetailsProps.trustedContactId = ''
  workerDetailsProps.viewerAccountId = currentAccount.globalId
  workerDetailsSidebarVisible.value = false
}

const openWorkerDetailPanel = (input: OpenWorkerDetailPanelInput) => {
  workerDetailsProps.workerId = input.workerAccountId
  if (input.viewerAccountId) {
    workerDetailsProps.viewerAccountId = input.viewerAccountId
  }
  workerDetailsSidebarVisible.value = true
}

addOpenWorkerDetailPanelListener(openWorkerDetailPanel)
</script>

<style lang="scss" scoped>
.graphql-table-wrapper {
  padding-top: 20px;
  background: white;
  .field-table {
    margin-top: 3rem;
    &:deep(.table-wrap) {
      max-height: 300px;
      overflow: auto;
    }
  }

  &.isFullscreen {
    padding-top: 0;
  }

  .query-table {
    margin-top: 3rem;
    &:deep(.table-wrap) {
      overflow: auto;
    }
  }
}
</style>
